import {
  MAX_RERUN_FAILED_TEST_CASES,
  RERUN_DEFAULT_ITERATIONS,
  RERUN_FAILED_DEFAULT_ITERATIONS,
} from "../model/constants"
import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import { readString, readStringArray } from "./actionArgumentReaders"

const MAX_ITERATIONS = 1000

function readExactRerunIds(
  value: unknown,
  pattern: RegExp,
): string[] | undefined {
  return Array.isArray(value) &&
    value.every(
      (id): id is string =>
        typeof id === "string" && id.length === 22 && pattern.test(id),
    )
    ? value
    : undefined
}

export function parseRunActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const problems: string[] = []
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only run-control actions.
  switch (action.kind) {
    case "launch_selected_run": {
      const test_set_id = readString(action.args, "test_set_id")
      const agent_id = readString(action.args, "agent_id")
      const persona_id = readString(action.args, "persona_id")
      const ids = action.args.test_case_ids
      const iterations = action.args.iterations
      const expected = action.args.expected_simulations
      if (
        !test_set_id ||
        !agent_id ||
        !persona_id ||
        !Array.isArray(ids) ||
        ids.length < 1 ||
        ids.length > 100 ||
        !ids.every(
          (id): id is string =>
            typeof id === "string" && id.length > 0 && id.trim() === id,
        ) ||
        new Set(ids).size !== ids.length ||
        typeof iterations !== "number" ||
        !Number.isInteger(iterations) ||
        iterations < 1 ||
        iterations > 50 ||
        typeof expected !== "number" ||
        !Number.isInteger(expected) ||
        expected !== ids.length * iterations
      ) {
        return {
          ok: false,
          problems: [
            "exact selected-case IDs, iterations, and expected simulation count must agree",
          ],
        }
      }
      return {
        ok: true,
        action: {
          kind: "launch_selected_run",
          args: {
            test_set_id,
            agent_id,
            persona_id,
            test_case_ids: ids,
            iterations,
            expected_simulations: expected,
            metric_ids: readStringArray(action.args, "metric_ids"),
          },
        },
      }
    }
    case "launch_run": {
      const test_set_id = readString(action.args, "test_set_id")
      const agent_id = readString(action.args, "agent_id")
      if (!test_set_id) {
        problems.push("missing test_set_id")
      }
      if (!agent_id) {
        problems.push("missing agent_id")
      }
      const iterations = action.args.iterations ?? 1
      if (
        typeof iterations !== "number" ||
        !Number.isInteger(iterations) ||
        iterations < 1 ||
        iterations > 50
      ) {
        problems.push("iterations must be an integer from 1 to 50")
      }
      if (problems.length > 0 || !test_set_id || !agent_id) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "launch_run",
          args: {
            test_set_id,
            agent_id,
            iterations: typeof iterations === "number" ? iterations : 1,
            persona_id: readString(action.args, "persona_id"),
            metric_ids: readStringArray(action.args, "metric_ids"),
            display_name: readString(action.args, "display_name"),
          },
        },
      }
    }
    case "rerun_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      const rawIterations = action.args.iterations
      let iterations = RERUN_DEFAULT_ITERATIONS
      if (typeof rawIterations === "number" && Number.isFinite(rawIterations)) {
        iterations = Math.min(
          MAX_ITERATIONS,
          Math.max(1, Math.round(rawIterations)),
        )
      }
      return {
        ok: true,
        action: { kind: "rerun_run", args: { run_id, iterations } },
      }
    }
    case "rerun_failed": {
      const run_id = readString(action.args, "run_id")
      const test_case_ids = readStringArray(action.args, "test_case_ids")
      if (!run_id) {
        problems.push("missing run_id")
      }
      if (!test_case_ids || test_case_ids.length === 0) {
        problems.push("missing test_case_ids")
      } else if (test_case_ids.length > MAX_RERUN_FAILED_TEST_CASES) {
        // Mirrors sofia-agent's max_items: fail closed rather than sending a
        // list the backend will reject.
        problems.push(
          `too many test_case_ids (${test_case_ids.length} > ${MAX_RERUN_FAILED_TEST_CASES})`,
        )
      }
      if (problems.length > 0 || !run_id || !test_case_ids) {
        return { ok: false, problems }
      }
      const rawFailedIterations = action.args.iterations
      let iterations = RERUN_FAILED_DEFAULT_ITERATIONS
      if (
        typeof rawFailedIterations === "number" &&
        Number.isFinite(rawFailedIterations)
      ) {
        iterations = Math.min(
          MAX_ITERATIONS,
          Math.max(1, Math.round(rawFailedIterations)),
        )
      }
      return {
        ok: true,
        action: {
          kind: "rerun_failed",
          args: { run_id, test_case_ids, iterations },
        },
      }
    }
    case "rerun_metrics": {
      const simulation_ids = readExactRerunIds(
        action.args.simulation_ids,
        /^[a-zA-Z0-9]{22}$/,
      )
      const metric_ids = readExactRerunIds(
        action.args.metric_ids,
        /^[a-zA-Z0-9-]{22}$/,
      )
      if (
        !simulation_ids?.length ||
        !metric_ids?.length ||
        simulation_ids.length > 100 ||
        metric_ids.length > 500 ||
        simulation_ids.length * metric_ids.length > 500
      ) {
        return {
          ok: false,
          problems: [
            "Choose exact simulation and metric IDs, with at most 500 evaluations.",
          ],
        }
      }
      return {
        ok: true,
        action: { kind: "rerun_metrics", args: { simulation_ids, metric_ids } },
      }
    }
    case "cancel_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      return { ok: true, action: { kind: "cancel_run", args: { run_id } } }
    }
    case "update_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      // tags is the full replacement set and must be present; an empty list is
      // the explicit "clear all tags" case.
      const tags = readStringArray(action.args, "tags")
      if (tags === undefined) {
        return { ok: false, problems: ["missing tags"] }
      }
      return {
        ok: true,
        action: { kind: "update_run", args: { run_id, tags } },
      }
    }
    case "attach_metrics_to_agent": {
      const agent_id = readString(action.args, "agent_id")
      const metric_ids = readStringArray(action.args, "metric_ids")
      if (!agent_id) {
        problems.push("missing agent_id")
      }
      if (!metric_ids || metric_ids.length === 0) {
        problems.push("missing metric_ids")
      }
      if (problems.length > 0 || !agent_id || !metric_ids) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "attach_metrics_to_agent",
          args: { agent_id, metric_ids },
        },
      }
    }
    default:
      return undefined
  }
}
